/* eslint-disable @typescript-eslint/no-explicit-any */
import { Injectable } from '@angular/core';
import {
  NewsRequestBody,
  Relevancy,
  ScoreEventWeights,
  SentimentFilter,
  SourceFilter,
  StoriesRequestBody,
} from '../models/news.interface';
import { QueryApiService } from '../services/customer-api/query-api.service';
import { QueryParams } from '../models/query-params.interface';
import {
  CommonDataParamsService,
  BaseContentPayloadParams,
} from './common-data-params.service';
import { FeatureFlagsService } from '../services/customer-api/feature-flags.service';

interface ContentPayloadParams extends BaseContentPayloadParams {
  skipSentimentFilter?: boolean;
  size?: number;
  from_index?: number;
  license?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class NewsRequestUtilService {
  /** Free-text keyword filter applied to outgoing news requests; written by news-feed component. */
  keywords = '';

  constructor(
    private commonDataParams: CommonDataParamsService,
    private featureFlagService: FeatureFlagsService,
    private queryApi: QueryApiService,
  ) {}

  // ========================================================================================
  // CONTENT PAYLOAD GENERATION METHODS
  // ========================================================================================

  /**
   * Generates the content payload based on custom parameters and query parameters
   */
  public generateContentPayload(
    customParams: ContentPayloadParams,
    storiesPayload = false,
  ): Record<string, any> {
    const queryParams = this.commonDataParams.currentQueryParams;
    const entities = this.commonDataParams.getEntities(
      queryParams,
      customParams.type,
    );
    const eventParticipants = entities.length > 0 ? entities[0].entities : [];
    const eventParticipantsBody =
      this.commonDataParams.hasEventParameters(queryParams) || storiesPayload;

    let result: Record<string, any> = {};

    // Handle event filter
    if (eventParticipantsBody || queryParams.participant) {
      const eventFilter = this.commonDataParams.createEventFilter(
        queryParams,
        eventParticipants,
      );

      if (eventFilter) {
        result.event_filter = eventFilter;
      }
    }

    // Handle specific content types
    if (customParams.type === 'search_query') {
      result = {
        ...result,
        ...this.generateSearchQueryContent(entities, customParams),
      };
    } else if (customParams.type === 'entities') {
      result = {
        ...result,
        ...this.generateEntitiesContent(
          entities,
          customParams,
          eventParticipantsBody,
        ),
      };
    }

    return result;
  }

  private generateSearchQueryContent(
    entities: { entities: string[] }[],
    customParams: ContentPayloadParams,
  ): Record<string, any> {
    const result: Record<string, any> = {};
    const query = this.queryApi.query;

    if (query?.query) {
      result.search_query = query.query;
    }

    const sentimentFilterParams = this.getSentimentFilterParams(customParams);
    if (sentimentFilterParams && entities.length > 0) {
      result.sentiment_filter = this.createSentimentFiltersWithParams(
        entities,
        sentimentFilterParams,
      );
    }

    return result;
  }

  private generateEntitiesContent(
    entities: { entities: string[] }[],
    customParams: ContentPayloadParams,
    hasEventParams: boolean,
  ): Record<string, any> {
    const result: Record<string, any> = {};

    if (!hasEventParams) {
      const sentimentFilterParams = this.getSentimentFilterParams(customParams);

      if (sentimentFilterParams) {
        if (entities.length > 0) {
          result.sentiment_filter = this.createSentimentFiltersWithParams(
            entities,
            sentimentFilterParams,
          ).filter((filter) => filter.entities.length > 0);
        }
      } else if (entities.length > 0) {
        result.sentiment_filter = entities;
      }
    }

    return result;
  }

  private getSentimentFilterParams(
    customParams: ContentPayloadParams,
  ): string | null {
    return customParams.skipSentimentFilter
      ? null
      : this.commonDataParams.currentQueryParams.filter;
  }

  private createSentimentFiltersWithParams(
    entities: { entities: string[] }[],
    sentimentParams: string,
  ): SentimentFilter[] {
    return entities.map((entityObj, index) => ({
      entities: entityObj.entities,
      sentiment: index === 0 ? sentimentParams.split(',') : undefined,
    }));
  }

  // ========================================================================================
  // COMMON PAYLOAD GENERATION METHODS
  // ========================================================================================

  /**
   * Generates default parameters for common request fields.
   */
  private generateCommonPayload(
    customParams: ContentPayloadParams,
  ): Partial<NewsRequestBody> {
    const queryParams: QueryParams = this.commonDataParams.currentQueryParams;
    const params: Partial<NewsRequestBody> = {
      project: this.commonDataParams.project,
    };

    params.time_range = this.commonDataParams.generateTimeRange(customParams);

    params.language_filter =
      this.commonDataParams.getLanguageFilter(queryParams);

    if (queryParams.categories) {
      params.document_category_filter = {
        exclude: queryParams.categories.split(','),
      };
    }

    if (queryParams.license === 'true' || customParams.license) {
      params.feed_filter = ['is_licensed_content'];
    }

    if (
      queryParams.continents ||
      queryParams.countries ||
      queryParams.ranks ||
      queryParams.panels
    ) {
      params.source_filter = this.createSourceFilter(queryParams);
    }

    if (customParams.size || queryParams.size) {
      params.size = customParams.size || queryParams.size;
    }
    if (customParams.from_index) {
      params.from_index = customParams.from_index;
    }
    if (this.keywords) {
      params.keyword_search = [this.keywords];
    }

    return params;
  }

  private createSourceFilter(queryParams: QueryParams): SourceFilter {
    const sourceFilter: SourceFilter = {};

    if (queryParams.continents) {
      sourceFilter.continents = {
        include: queryParams.continents.split(','),
      };
    }

    if (queryParams.countries) {
      sourceFilter.countries = {
        include: queryParams.countries.split(','),
      };
    }

    if (queryParams.ranks) {
      sourceFilter.ranks = {
        include: queryParams.ranks.split(',').map(Number),
      };
    }

    if (queryParams.panels) {
      sourceFilter.panels = {
        include: queryParams.panels.split(','),
      };
    }

    return sourceFilter;
  }

  // ========================================================================================
  // RELEVANCY AND SCORING METHODS
  // ========================================================================================

  private getRelevancyDefaults(type: string): Relevancy {
    const relevancy: Relevancy = {};

    if (type === 'news') {
      relevancy.entity_boost = 0;
      relevancy.high_entity_article_threshold = 0;
    } else if (type === 'top_news') {
      relevancy.sentence_with_entity_threshold =
        this.commonDataParams.currentQueryParams
          .sentence_with_entity_threshold ?? 0.35;
      relevancy.entity_span_threshold =
        this.commonDataParams.currentQueryParams.entity_span_threshold ?? 0.9;
      relevancy.high_entity_article_threshold =
        this.commonDataParams.currentQueryParams
          .high_entity_article_threshold ?? 0.0;
      relevancy.elasticsearch_score_decay =
        this.commonDataParams.currentQueryParams.elasticsearch_score_decay ??
        0.5;
      relevancy.entity_boost =
        this.commonDataParams.currentQueryParams.entity_boost ?? 100;
    }

    return relevancy;
  }

  private getScoreEventWeights(): ScoreEventWeights {
    const scoreEvent: ScoreEventWeights = {};
    const eventWeights: Record<string, number> = {};

    const defaultScore = this.featureFlagService.scorelabSettings.defaultScore;
    const customScores = this.featureFlagService.scorelabSettings.customScores;

    const predefinedScores = [
      'esg',
      'general_risk',
      'e_standard',
      's_standard',
      'g_standard',
      'esg_risk',
      'immediate_risk',
      'credit_watch',
      'credit_risk',
      'lksg_risk',
      'eb_sim',
      'eb_sim_risk',
    ];

    if (defaultScore && predefinedScores.includes(defaultScore)) {
      scoreEvent.score_type =
        defaultScore === 'credit_risk' ? 'credit_watch' : defaultScore;
    } else if (defaultScore && customScores) {
      const defaultScoreObj = customScores.find(
        (score) => score.uid === defaultScore,
      );

      if (defaultScoreObj?.event_weights?.length) {
        defaultScoreObj.event_weights.forEach((event) => {
          const event_id = event.event_id;
          const weight = event.weight ?? 5;
          const polarity = event.polarity ?? -1;

          if (event_id) {
            eventWeights[`${event_id}_weight`] = weight * polarity;
          }
        });

        if (Object.keys(eventWeights).length) {
          scoreEvent.event_weights = eventWeights;
        }
      }
    }

    return scoreEvent;
  }

  // ========================================================================================
  // PUBLIC API REQUEST BUILDERS
  // ========================================================================================

  /**
   * Creates a request body for the News API.
   */
  createNewsRequest(
    customParams: Partial<NewsRequestBody> & ContentPayloadParams,
    contentPayload: ContentPayloadParams,
  ): NewsRequestBody {
    const defaultParams = this.generateCommonPayload(contentPayload);
    const content = this.generateContentPayload(contentPayload);
    const relevancyDefaults = this.getRelevancyDefaults('news');

    return {
      ...defaultParams,
      ...content,
      relevancy: {
        ...relevancyDefaults,
      },
      ...customParams,
    } as NewsRequestBody;
  }

  /**
   * Creates a request body for the Stories API.
   */
  createStoriesRequest(
    customParams: Partial<StoriesRequestBody> & ContentPayloadParams,
    contentPayload: ContentPayloadParams,
  ): StoriesRequestBody {
    const defaultParams = this.generateCommonPayload(contentPayload);
    const content = this.generateContentPayload(contentPayload, true);
    return {
      ...defaultParams,
      ...customParams,
      ...content,
      min_size: customParams.min_size || 4,
    } as StoriesRequestBody;
  }

  createTopNewsRequest(
    customParams: Partial<StoriesRequestBody> & ContentPayloadParams,
    contentPayload: ContentPayloadParams,
  ): StoriesRequestBody {
    const relevancyDefaults = this.getRelevancyDefaults('top_news');
    const scoreEventWeights = this.getScoreEventWeights();
    const defaultParams = this.generateCommonPayload(contentPayload);
    const content = this.generateContentPayload(contentPayload, true);
    return {
      ...defaultParams,
      ...customParams,
      ...content,
      relevancy: {
        ...relevancyDefaults,
        ...scoreEventWeights,
      },
      min_size: 4,
    } as StoriesRequestBody;
  }
}
